import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AlertTriangle, RefreshCw, TrendingUp, Sparkles, Bookmark, BookmarkCheck, EyeOff, Eye, Search, X, StickyNote } from "lucide-react";
import { ScoreExplanation } from "@/components/trends/ScoreExplanation";
import { SourceTransparency } from "@/components/trends/SourceTransparency";
import { CompetitorOverlay, type CompetitorsAnalysis } from "@/components/trends/CompetitorOverlay";
import { ValidationTools } from "@/components/trends/ValidationTools";
import { DetailedBlueprint, type DetailedBlueprintData } from "@/components/trends/DetailedBlueprint";
import { ShareBlueprintButton } from "@/components/trends/ShareBlueprintButton";
import { useToast } from "@/hooks/use-toast";

import { useApprovedTrends, type ApprovedTrend } from "@/hooks/useApprovedTrends";
import { FOCUS_TREND_EVENT, formatDataAge } from "@/lib/trendNotifications";

type LiveTrend = ApprovedTrend;

type ActionRow = {
  id: string;
  trend_id: string;
  action: "bookmark" | "dismiss";
  note: string | null;
};

type ViewMode = "all" | "saved" | "dismissed";
type SortKey = "recent" | "score" | "velocity";

export function LiveTrendsFeed() {
  const { toast } = useToast();
  const { trends, loading: trendsLoading, error: trendsError, stale: trendsStale, updatedAt, refetch: refetchTrends } = useApprovedTrends();
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [actions, setActions] = useState<Record<string, ActionRow>>({});
  const [view, setView] = useState<ViewMode>("all");
  const [sort, setSort] = useState<SortKey>("recent");
  const [minScore, setMinScore] = useState(0);
  const [niche, setNiche] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [noteOpen, setNoteOpen] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const errorCardRef = useRef<HTMLElement | null>(null);
  const retryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    loadActions();
  }, []);

  // Focus management: move focus to the error card when it appears so keyboard
  // and screen-reader users land on the failure and its Retry action.
  useEffect(() => {
    if (trendsError) errorCardRef.current?.focus();
  }, [trendsError]);


  // Toast "View trend" action: reveal, scroll to, highlight and focus the card.
  useEffect(() => {
    const onFocusTrend = (e: Event) => {
      const trendId = (e as CustomEvent<{ trendId: string }>).detail?.trendId;
      if (!trendId) return;
      setView("all");
      setQuery("");
      setNiche("all");
      setMinScore(0);
      setHighlighted(trendId);
      requestAnimationFrame(() => {
        const el = document.getElementById(`trend-${trendId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus();
      });
    };
    window.addEventListener(FOCUS_TREND_EVENT, onFocusTrend as EventListener);
    return () => window.removeEventListener(FOCUS_TREND_EVENT, onFocusTrend as EventListener);
  }, []);

  const loadActions = async () => {
    const { data } = await supabase
      .from("user_trend_actions")
      .select("id, trend_id, action, note");
    const map: Record<string, ActionRow> = {};
    (data ?? []).forEach((r: any) => { map[r.trend_id] = r as ActionRow; });
    setActions(map);
  };

  const setAction = async (trendId: string, action: "bookmark" | "dismiss" | null, note?: string) => {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) { toast({ title: "Sign in required", variant: "destructive" }); return; }
    if (action === null) {
      await supabase.from("user_trend_actions").delete().eq("trend_id", trendId).eq("user_id", auth.user.id);
    } else {
      await supabase.from("user_trend_actions").upsert({
        user_id: auth.user.id,
        trend_id: trendId,
        action,
        note: note ?? actions[trendId]?.note ?? null,
      }, { onConflict: "user_id,trend_id" });
    }
    loadActions();
  };

  const toggleBookmark = (t: LiveTrend) => {
    const cur = actions[t.id];
    if (cur?.action === "bookmark") setAction(t.id, null);
    else setAction(t.id, "bookmark");
  };
  const toggleDismiss = (t: LiveTrend) => {
    const cur = actions[t.id];
    if (cur?.action === "dismiss") setAction(t.id, null);
    else setAction(t.id, "dismiss");
  };

  const openNote = (t: LiveTrend) => {
    setNoteDraft(actions[t.id]?.note ?? "");
    setNoteOpen(t.id);
  };
  const saveNote = async () => {
    if (!noteOpen) return;
    const cur = actions[noteOpen];
    await setAction(noteOpen, cur?.action ?? "bookmark", noteDraft);
    setNoteOpen(null);
    toast({ title: "Note saved" });
  };

  const niches = useMemo(() => {
    const s = new Set<string>();
    (trends ?? []).forEach((t) => t.niche && s.add(t.niche));
    return Array.from(s).sort();
  }, [trends]);

  const filtered = useMemo(() => {
    if (!trends) return [];
    let list = trends.filter((t) => {
      const a = actions[t.id];
      if (view === "all") return a?.action !== "dismiss";
      if (view === "saved") return a?.action === "bookmark";
      if (view === "dismissed") return a?.action === "dismiss";
      return true;
    });
    if (niche !== "all") list = list.filter((t) => t.niche === niche);
    if (minScore > 0) list = list.filter((t) => t.total_score >= minScore);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((t) =>
        t.title.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    }
    if (sort === "score") list = [...list].sort((a, b) => b.total_score - a.total_score);
    else if (sort === "velocity") list = [...list].sort((a, b) => b.velocity_score - a.velocity_score);
    else list = [...list].sort((a, b) => (b.published_at ?? "").localeCompare(a.published_at ?? ""));
    return list;
  }, [trends, actions, view, niche, minScore, query, sort]);

  if (trendsError) {
    return (
      <section
        className="rounded-xl border border-destructive/40 bg-destructive/5 p-5 space-y-3"
        data-testid="curated-feed-error"
        role="alert"
        aria-labelledby="curated-feed-error-title"
        aria-describedby="curated-feed-error-desc"
        tabIndex={-1}
        ref={errorCardRef}
      >
        <h2
          id="curated-feed-error-title"
          className="flex items-center gap-2 font-mono text-xs text-destructive"
        >
          <AlertTriangle className="size-4" aria-hidden="true" /> CURATED FEED UNAVAILABLE
        </h2>
        <p id="curated-feed-error-desc" className="text-sm text-muted-foreground">
          We couldn&apos;t load your curated trends. {trendsError}
        </p>
        <button
          type="button"
          onClick={() => refetchTrends()}
          data-testid="curated-feed-retry"
          ref={retryRef}
          aria-label="Retry loading curated trends"
          aria-busy={trendsLoading}
          disabled={trendsLoading}
          className="inline-flex min-h-11 items-center gap-1.5 rounded border border-border bg-background px-3 py-1.5 text-xs font-mono uppercase hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
        >
          <RefreshCw
            className={`size-3.5 ${trendsLoading ? "animate-spin" : ""}`}
            aria-hidden="true"
          />{" "}
          {trendsLoading ? "Retrying" : "Retry"}
        </button>
        <p className="sr-only" role="status">
          {trendsLoading ? "Retrying to load curated trends" : "Curated trends failed to load"}
        </p>
      </section>
    );
  }


  if (trendsLoading || trends === null) return null;

  const counts = {
    all: trends.filter((t) => actions[t.id]?.action !== "dismiss").length,
    saved: trends.filter((t) => actions[t.id]?.action === "bookmark").length,
    dismissed: trends.filter((t) => actions[t.id]?.action === "dismiss").length,
  };

  const tabs: { key: ViewMode; label: string; count: number; Icon: any }[] = [
    { key: "all", label: "All", count: counts.all, Icon: Sparkles },
    { key: "saved", label: "Saved", count: counts.saved, Icon: BookmarkCheck },
    { key: "dismissed", label: "Dismissed", count: counts.dismissed, Icon: EyeOff },
  ];

  return (
    <section className="space-y-4" data-testid="curated-feed">
      {trendsStale && (
        <div
          data-testid="curated-feed-stale"
          role="status"
          className="rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2"
        >
          <AlertTriangle className="size-3.5" aria-hidden="true" />
          Showing your last known curated trends — live data is temporarily unavailable.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span data-testid="curated-feed-age" role="status">
          {trendsStale ? "Cached data from " : "Updated "}
          {updatedAt ? formatDataAge(updatedAt) : "unknown"}
        </span>
        <button
          type="button"
          onClick={() => refetchTrends()}
          data-testid="curated-feed-refresh"
          aria-label="Refresh curated trends"
          aria-busy={trendsLoading}
          disabled={trendsLoading}
          className="inline-flex min-h-11 items-center gap-1.5 rounded border border-border bg-background px-3 py-1.5 font-mono uppercase text-[11px] hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
        >
          <RefreshCw className={`size-3.5 ${trendsLoading ? "animate-spin" : ""}`} aria-hidden="true" />
          {trendsLoading ? "Refreshing" : "Refresh"}
        </button>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-xs text-primary mb-1 flex items-center gap-2">
            <Sparkles className="size-3" /> CURATED · FROM 7 LIVE SOURCES
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold">Fresh trends, scored &amp; blueprinted</h2>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map(({ key, label, count, Icon }) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-mono uppercase text-xs border-b-2 -mb-px transition ${
              view === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="size-3.5" />
            {label}
            <span className="text-[10px] rounded-full bg-secondary px-1.5 py-0.5">{count}</span>
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-lg border border-border bg-card p-3">
        <div className="col-span-2 sm:col-span-1 relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search keywords…"
            className="w-full rounded border border-border bg-background pl-7 pr-2 py-1.5 text-xs"
          />
        </div>
        <select
          value={niche}
          onChange={(e) => setNiche(e.target.value)}
          className="rounded border border-border bg-background px-2 py-1.5 text-xs"
        >
          <option value="all">All industries</option>
          {niches.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="rounded border border-border bg-background px-2 py-1.5 text-xs"
        >
          <option value="recent">Most recent</option>
          <option value="score">Highest score</option>
          <option value="velocity">Highest velocity</option>
        </select>
        <label className="flex items-center gap-2 text-xs">
          <span className="font-mono text-[10px] text-muted-foreground shrink-0">min {minScore}</span>
          <input
            type="range" min={0} max={100} step={5}
            value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="w-full accent-primary"
          />
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          {trends.length === 0 ? (
            <>
              <div className="font-display text-base text-foreground mb-1">No curated trends yet</div>
              Our scanners are still pulling signals. Fresh, scored trends appear here as soon as they're approved.
            </>
          ) : view === "saved" ? (
            "No saved trends yet. Bookmark trends to revisit them here."
          ) : view === "dismissed" ? (
            "No dismissed trends."
          ) : (
            <>
              No trends match your filters.{" "}
              <button
                onClick={() => { setQuery(""); setNiche("all"); setMinScore(0); }}
                className="underline text-foreground hover:text-primary"
              >
                Reset filters
              </button>
            </>
          )}
        </div>
      ) : (

        <div className="grid sm:grid-cols-2 gap-4">
          {filtered.map((t) => {
            const bp = t.blueprint || {};
            const a = actions[t.id];
            const isBookmarked = a?.action === "bookmark";
            const isDismissed = a?.action === "dismiss";
            return (
              <article
                key={t.id}
                id={`trend-${t.id}`}
                tabIndex={-1}
                data-highlighted={highlighted === t.id ? "true" : undefined}
                className={`rounded-xl border bg-card p-4 sm:p-5 hover:border-primary/40 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  highlighted === t.id ? "ring-2 ring-primary border-primary" : ""
                } ${isBookmarked ? "border-primary/50" : isDismissed ? "border-border opacity-60" : "border-border"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1 truncate">
                      {t.niche ?? "trend"} · {t.source_slugs.slice(0, 3).join(" · ")}
                    </div>
                    <h3 className="font-display text-base sm:text-lg font-semibold leading-snug">{t.title}</h3>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-xl sm:text-2xl font-bold text-primary flex items-center gap-1">
                      <TrendingUp className="size-4" />{t.total_score}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">v{t.velocity_score}</div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{t.summary}</p>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  <button
                    onClick={() => toggleBookmark(t)}
                    className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-mono uppercase transition ${
                      isBookmarked ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {isBookmarked ? <BookmarkCheck className="size-3" /> : <Bookmark className="size-3" />}
                    {isBookmarked ? "saved" : "save"}
                  </button>
                  <button
                    onClick={() => toggleDismiss(t)}
                    className={`flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-mono uppercase transition ${
                      isDismissed ? "border-destructive/50 bg-destructive/10 text-destructive" : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {isDismissed ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
                    {isDismissed ? "restore" : "dismiss"}
                  </button>
                  <ShareBlueprintButton trendId={t.id} title={t.title} />
                  {a && (
                    <button
                      onClick={() => openNote(t)}
                      className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-mono uppercase text-muted-foreground hover:text-foreground"
                    >
                      <StickyNote className="size-3" />
                      {a.note ? "edit note" : "add note"}
                    </button>
                  )}
                </div>

                {a?.note && (
                  <div className="mt-2 rounded-md border border-border/60 bg-secondary/30 p-2 text-xs text-muted-foreground italic">
                    “{a.note}”
                  </div>
                )}

                {bp?.target_buyer && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    <b className="text-foreground">Buyer:</b> {bp.target_buyer} ·{" "}
                    <b className="text-foreground">Price:</b> {bp.price_range}
                  </div>
                )}
                {t.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {t.tags.slice(0, 5).map((tag) => (
                      <span key={tag} className="font-mono text-[10px] rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                <ScoreExplanation
                  velocity={t.velocity_score}
                  volume={t.volume_score}
                  relevance={t.relevance_score}
                  sourceSlugs={t.source_slugs}
                  breakdown={bp?.score_breakdown}
                />
                <SourceTransparency sourceSlugs={t.source_slugs} />
                {bp?.competitors_analysis && (
                  <CompetitorOverlay data={bp.competitors_analysis as CompetitorsAnalysis} trendTitle={t.title} />
                )}
                <DetailedBlueprint trendId={t.id} initial={bp?.detailed as DetailedBlueprintData | undefined} />
                <ValidationTools trendId={t.id} trendTitle={t.title} tags={t.tags} />
              </article>
            );
          })}
        </div>
      )}

      {noteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4" onClick={() => setNoteOpen(null)}>
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold">Your note</h3>
              <button onClick={() => setNoteOpen(null)} className="text-muted-foreground hover:text-foreground"><X className="size-4" /></button>
            </div>
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Why does this trend matter? Angles, audience, next steps…"
              className="w-full min-h-[120px] rounded border border-border bg-background p-2 text-sm"
              autoFocus
            />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setNoteOpen(null)} className="text-xs font-mono uppercase text-muted-foreground hover:text-foreground">cancel</button>
              <button onClick={saveNote} className="rounded bg-primary px-3 py-1.5 text-xs font-mono uppercase text-primary-foreground">save</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
